<template>
  <ion-page>
    <div v-if="sessionRestoring" class="login-boot-bridge" aria-busy="true" aria-live="polite">
      <img :src="logoSrc" alt="Liga" class="login-boot-logo" />
      <ion-spinner name="crescent" class="login-boot-spinner"></ion-spinner>
      <p class="login-boot-text">Загрузка…</p>
    </div>
    <ion-content
      v-show="!authenticated && !sessionRestoring"
      :class="'main-background'"
      :fullscreen="true"
      slot="fixed"
      :scroll-y="true"
    >
      <div class="logo" v-show="showLogo">
        <img :src="logoSrc" alt="liga-logo" />
      </div>
      <div class="login-card-wrapper">
        <ion-card :class="['bg-transparent', showLogo ? '' : 'half-screen-top']">
          <ion-card-header class="ion-text-center white-bold">АВТОРИЗАЦИЯ</ion-card-header>
          <ion-card-content>
            <!-- OTP: email -->
            <template v-if="authMode === 'otp-email'">
              <ion-input
                class="login-field"
                label="Электронная почта"
                label-placement="floating"
                fill="solid"
                type="email"
                inputmode="email"
                v-model="loginData.username"
                :disabled="inlineLoading"
                @ionFocus="onInputFocus()"
                autocomplete="email"
                @keyup.enter="requestOtp"
              ></ion-input>
              <p class="otp-email-hint">Код придёт на почту — и для нового адреса (это регистрация), и для уже существующего.</p>
              <div class="main-button-container">
                <ion-button @click="requestOtp()" :disabled="inlineLoading" type="submit">
                  <p v-show="!inlineLoading">Получить код</p>
                  <ion-spinner name="bubbles" v-show="inlineLoading"></ion-spinner>
                </ion-button>
              </div>
              <div class="auth-switch" @click="switchToPassword">Войти по паролю</div>
            </template>

            <!-- OTP: 6 digits -->
            <template v-else-if="authMode === 'otp-code'">
              <p class="otp-hint">Код отправлен на {{ loginData.username }}</p>
              <div class="otp-boxes">
                <input
                  v-for="(_, idx) in otpDigits"
                  :key="idx"
                  :ref="el => setOtpRef(el, idx)"
                  class="otp-box"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="6"
                  enterkeyhint="done"
                  :autocomplete="idx === 0 ? 'one-time-code' : 'off'"
                  :disabled="inlineLoading"
                  :value="otpDigits[idx]"
                  @input="onOtpInput(idx, $event)"
                  @beforeinput="onOtpBeforeInput(idx, $event)"
                  @keydown="onOtpKeydown(idx, $event)"
                  @paste="onOtpPaste"
                  @focus="onInputFocus()"
                />
              </div>
              <p v-if="otpError" class="otp-error">{{ otpError }}</p>
              <div class="main-button-container">
                <ion-button @click="verifyOtp()" :disabled="inlineLoading || !otpComplete" type="submit">
                  <p v-show="!inlineLoading">Войти</p>
                  <ion-spinner name="bubbles" v-show="inlineLoading"></ion-spinner>
                </ion-button>
              </div>
              <div class="otp-actions">
                <button
                  type="button"
                  class="auth-link-btn"
                  :disabled="inlineLoading || resendSeconds > 0"
                  @click="requestOtp(true)"
                >
                  {{ resendSeconds > 0 ? `Отправить снова (${resendSeconds})` : 'Отправить снова' }}
                </button>
                <button type="button" class="auth-link-btn" :disabled="inlineLoading" @click="backToOtpEmail">
                  Изменить email
                </button>
              </div>
              <div class="auth-switch" @click="switchToPassword">Войти по паролю</div>
            </template>

            <!-- Password fallback -->
            <template v-else>
              <ion-input
                class="login-field"
                label="Логин (email)"
                label-placement="floating"
                fill="solid"
                type="text"
                v-model="loginData.username"
                :disabled="inlineLoading"
                @ionFocus="onInputFocus()"
                autocomplete="username"
                @keyup.enter="singIn"
              ></ion-input>
              <ion-input
                class="login-field"
                label="Пароль"
                label-placement="floating"
                fill="solid"
                type="password"
                v-model="loginData.password"
                :disabled="inlineLoading"
                @ionFocus="onInputFocus()"
                autocomplete="current-password"
                @keyup.enter="singIn"
              ></ion-input>
              <div class="main-button-container">
                <ion-button @click="singIn()" :disabled="inlineLoading" type="submit">
                  <p v-show="!inlineLoading">Вход</p>
                  <ion-spinner name="bubbles" v-show="inlineLoading"></ion-spinner>
                </ion-button>
              </div>
              <div class="auth-switch" @click="switchToOtpEmail">Войти по коду из почты</div>
            </template>

            <div class="signup-container">
              <div @click="navigate('signup')">Регистрация</div>
              <div @click="navigate('reset-password')">Забыли пароль?</div>
            </div>
            <div class="not-authorized" @click="navigate('showcase')">Пропустить</div>
          </ion-card-content>
        </ion-card>
      </div>
      <div class="login-legal">
        <div class="login-legal-links">
          <a :href="LEGAL.offerUrl" target="_blank" rel="noopener noreferrer" class="login-legal-link">Договор оферты</a>
          <span @click="navigate('privacy-policy')" class="login-legal-link">Политика конфиденциальности</span>
          <span @click="navigate('offer-supplement')" class="login-legal-link">Условия возврата</span>
        </div>
        <div class="login-legal-payee">
          <p class="login-legal-payee-title">Платежи принимает:</p>
          <p class="login-legal-payee-name">{{ LEGAL.payee.fullName }}</p>
          <p>ИНН {{ LEGAL.payee.inn }} · ОГРНИП {{ LEGAL.payee.ogrnip }}</p>
          <p>{{ LEGAL.payee.address }}</p>
          <p>Тел.: {{ LEGAL.payee.phone }} · {{ LEGAL.payee.email }}</p>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import router from "@/router/index";
import AccountService from "@/services/account.service";
import { hideNativeSplash } from "@/services/native-splash.service";
import { flushPendingPushOpen, registerAdminPushNotifications } from "@/services/push.service";
import { flushPendingDeepLink } from "@/services/deep-link.service";
import store from "@/store/index";
import { Browser } from "@capacitor/browser";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { stripAuthWhitespace, isValidEmail } from "@/utils/auth-input";
import { getClientDeviceMeta } from "@/utils/client-device-meta";
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonContent,
    IonInput,
    IonPage,
    IonSpinner,
    alertController,
    toastController,
} from "@ionic/vue";
import { computed, defineComponent, inject, nextTick } from "vue";
import { LEGAL } from "@/config/legal";
import logoBlack from "@/theme/liga-logo-black.png";
import logoWhite from "@/theme/liga-logo-white.png";

type AuthMode = "otp-email" | "otp-code" | "password";

export default defineComponent({
  props: {
    timeout: { type: Number, default: 1000 },
  },
  name: "Login",
  components: {
    IonContent,
    IonPage,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonInput,
    IonButton,
    IonSpinner,
  },
  setup() {
    const accountService = inject("accountService") as AccountService;
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;
    const logoSrc = computed(() =>
      (globalContext.effectiveTheme || "dark") === "light" ? logoBlack : logoWhite
    );

    return {
      accountService,
      isMobile,
      globalContext,
      LEGAL,
      logoSrc,
    };
  },
  data() {
    return {
      loginData: {
        username: "",
        password: "",
      },
      authMode: "otp-email" as AuthMode,
      otpDigits: ["", "", "", "", "", ""] as string[],
      otpInputEls: [] as Array<HTMLInputElement | null>,
      otpError: "",
      resendSeconds: 0,
      resendTimer: 0 as number | ReturnType<typeof setInterval>,
      lastOtpBootstrapKey: '',
      showLogo: true,
      inlineLoading: false,
      sessionRestoring: Capacitor.isNativePlatform(),
    };
  },
  computed: {
    account() {
      return store.getters.account;
    },
    authenticated() {
      return store.getters.authenticated;
    },
    otpComplete(): boolean {
      return this.otpDigits.every(d => /^\d$/.test(d));
    },
    otpCode(): string {
      return this.otpDigits.join("");
    },
  },
  created() {
    this.inlineLoading = true;
    this.accountService
      .hasAnyAuthorityAndCheckAuth(process.env.VUE_APP_INSTRUCTOR_ROLE)
      .then((response) => {
        this.inlineLoading = false;
        if (response) {
          this.successLogin();
        } else {
          Preferences.remove({ key: 'jwt' });
          store.commit('logout');
          this.finishSessionRestore();
          return;
        }
      })
      .catch(() => {
        this.inlineLoading = false;
        this.finishSessionRestore();
      });
  },
  ionViewWillEnter() {
    this.showLogo = true;
    if (this.globalContext.offlineMode) {
      this.finishSessionRestore();
      router.replace('/showcase');
      return;
    }
    const sessionExpired = this.$route.query.sessionExpired === '1';
    if (sessionExpired) {
      router.replace({ path: '/login', query: {} });
      toastController.create({
        message: 'Сессия истекла. Войдите снова.',
        duration: 4000,
        color: 'warning',
      }).then(t => t.present());
    }
    void this.bootstrapFromQuery();
  },
  ionViewDidLeave() {
    this.clearResendTimer();
  },
  beforeUnmount() {
    this.clearResendTimer();
  },
  methods: {
    finishSessionRestore() {
      this.sessionRestoring = false;
      void hideNativeSplash();
    },

    navigate(routeName: string): void {
      void hideNativeSplash();
      router.push(`/${routeName}`);
    },

    onInputFocus() {
      if (this.isMobile) this.showLogo = false;
    },

    onInputBlur() {
      if (this.isMobile) this.showLogo = true;
    },

    async bootstrapFromQuery() {
      if (this.authenticated) return;
      const emailRaw = this.$route.query.email;
      const wantOtp = this.$route.query.otp === '1';
      if (typeof emailRaw === 'string' && emailRaw.trim()) {
        this.loginData.username = stripAuthWhitespace(emailRaw).toLowerCase();
      }
      if (!(wantOtp && this.loginData.username)) return;
      const key = `${this.loginData.username}|otp`;
      if (this.lastOtpBootstrapKey === key) return;
      this.lastOtpBootstrapKey = key;
      await this.requestOtp(true);
    },

    switchToPassword() {
      this.authMode = 'password';
      this.otpError = '';
      this.clearOtpDigits();
    },

    switchToOtpEmail() {
      this.authMode = 'otp-email';
      this.otpError = '';
      this.loginData.password = '';
    },

    backToOtpEmail() {
      this.authMode = 'otp-email';
      this.otpError = '';
      this.clearOtpDigits();
      this.clearResendTimer();
    },

    clearOtpDigits() {
      this.otpDigits = ['', '', '', '', '', ''];
    },

    setOtpRef(el: unknown, idx: number) {
      if (!this.otpInputEls) this.otpInputEls = [];
      this.otpInputEls[idx] = (el as HTMLInputElement) || null;
    },

    focusOtp(idx: number) {
      void nextTick(() => {
        const el = this.otpInputEls[idx];
        if (el) el.focus();
      });
    },

    startResendCooldown(seconds = 60) {
      this.clearResendTimer();
      this.resendSeconds = seconds;
      this.resendTimer = setInterval(() => {
        if (this.resendSeconds <= 1) {
          this.clearResendTimer();
          this.resendSeconds = 0;
          return;
        }
        this.resendSeconds -= 1;
      }, 1000);
    },

    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer as number);
        this.resendTimer = 0;
      }
    },

    onOtpInput(idx: number, event: Event) {
      const input = event.target as HTMLInputElement;
      const raw = (input.value || '').replace(/\D/g, '');
      // iOS autofill / paste кладёт весь код в одну ячейку (maxlength=1 это ломал).
      if (raw.length > 1) {
        this.applyOtpString(raw);
        return;
      }
      const digit = raw.slice(-1);
      this.otpDigits.splice(idx, 1, digit);
      input.value = digit;
      this.otpError = '';
      if (digit && idx < 5) {
        this.focusOtp(idx + 1);
      } else if (digit && idx === 5 && this.otpComplete) {
        void this.verifyOtp();
      }
    },

    onOtpBeforeInput(idx: number, event: Event) {
      const e = event as InputEvent;
      const data = e.data || '';
      const digits = data.replace(/\D/g, '');
      if (digits.length > 1) {
        e.preventDefault();
        this.applyOtpString(
          (this.otpDigits.slice(0, idx).join('') + digits).slice(0, 6)
        );
      }
    },

    onOtpKeydown(idx: number, event: KeyboardEvent) {
      if (event.key === 'Backspace') {
        if (this.otpDigits[idx]) {
          this.otpDigits.splice(idx, 1, '');
          return;
        }
        if (idx > 0) {
          event.preventDefault();
          this.otpDigits.splice(idx - 1, 1, '');
          this.focusOtp(idx - 1);
        }
      } else if (event.key === 'ArrowLeft' && idx > 0) {
        event.preventDefault();
        this.focusOtp(idx - 1);
      } else if (event.key === 'ArrowRight' && idx < 5) {
        event.preventDefault();
        this.focusOtp(idx + 1);
      }
    },

    onOtpPaste(event: ClipboardEvent) {
      const text = event.clipboardData?.getData('text') || '';
      const digits = text.replace(/\D/g, '');
      if (!digits) return;
      event.preventDefault();
      this.applyOtpString(digits);
    },

    applyOtpString(raw: string) {
      const digits = raw.replace(/\D/g, '').slice(0, 6).split('');
      this.otpDigits = [0, 1, 2, 3, 4, 5].map(i => digits[i] || '');
      this.otpError = '';
      void nextTick(() => {
        this.otpInputEls.forEach((el, i) => {
          if (el) el.value = this.otpDigits[i] || '';
        });
        const nextEmpty = this.otpDigits.findIndex(d => !d);
        this.focusOtp(nextEmpty >= 0 ? nextEmpty : 5);
      });
      if (this.otpDigits.every(d => /^\d$/.test(d))) {
        void this.verifyOtp();
      }
    },

    problemMessage(data: unknown, fallback: string): string {
      const body = data as { title?: string; detail?: string; message?: string } | null;
      if (body?.title && !body.title.startsWith('error.')) return body.title;
      if (body?.detail && !String(body.detail).startsWith('error.')) return body.detail;
      return fallback;
    },

    async requestOtp(silent = false) {
      const email = stripAuthWhitespace(this.loginData.username).toLowerCase();
      this.loginData.username = email;
      if (!isValidEmail(email)) {
        this.showAlert('Ошибка', 'Укажите корректный email');
        return;
      }
      this.showLogo = true;
      this.inlineLoading = true;
      this.otpError = '';
      try {
        const response = await CapacitorHttp.post({
          url: `${process.env.VUE_APP_API_URL}api/authenticate/otp/request`,
          data: { email },
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.status >= 400) {
          const msg = this.problemMessage(response.data, 'Не удалось отправить код');
          if (!silent) this.showAlert('Ошибка', msg);
          else this.otpError = msg;
          return;
        }
        this.authMode = 'otp-code';
        this.clearOtpDigits();
        this.startResendCooldown(60);
        await nextTick();
        this.focusOtp(0);
        if (!silent) {
          const toast = await toastController.create({
            message: 'Код отправлен на почту',
            duration: 2200,
            position: 'bottom',
            color: 'success',
          });
          await toast.present();
        }
      } catch (err: any) {
        if (err?.message === 'Network Error' || err?.status === 408) {
          this.$router.replace('/badconnection');
        } else {
          const msg = this.problemMessage(err?.data || err?.response?.data, 'Не удалось отправить код');
          this.showAlert('Ошибка', msg);
        }
      } finally {
        this.inlineLoading = false;
      }
    },

    async verifyOtp() {
      if (!this.otpComplete || this.inlineLoading) return;
      this.inlineLoading = true;
      this.otpError = '';
      const email = stripAuthWhitespace(this.loginData.username).toLowerCase();
      const meta = await getClientDeviceMeta();
      try {
        const response = await CapacitorHttp.post({
          url: `${process.env.VUE_APP_API_URL}api/authenticate/otp/verify`,
          data: {
            email,
            code: this.otpCode,
            rememberMe: true,
            deviceName: meta.deviceName || undefined,
            platform: meta.platform || undefined,
            appVersion: meta.appVersion || undefined,
            appBuild: meta.appBuild || undefined,
          },
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.status !== 200 && response.status !== 1001) {
          this.otpError = this.problemMessage(response.data, 'Неверный код');
          this.clearOtpDigits();
          this.focusOtp(0);
          return;
        }
        await this.applyJwtFromResponse(response);
      } catch (err: any) {
        if (err?.message === 'Network Error' || err?.status === 408) {
          this.$router.replace('/badconnection');
        } else {
          this.otpError = this.problemMessage(err?.data || err?.response?.data, 'Неверный код');
          this.clearOtpDigits();
          this.focusOtp(0);
        }
      } finally {
        this.inlineLoading = false;
      }
    },

    async applyJwtFromResponse(response: { headers: Record<string, string>; data?: unknown }) {
      await this.accountService.persistJwtFromAuthResponse(response);
      this.successLogin();
    },

    async singIn() {
      this.showLogo = true;
      this.inlineLoading = true;

      const username = stripAuthWhitespace(this.loginData.username).toLowerCase();
      const password = stripAuthWhitespace(this.loginData.password);
      this.loginData.username = username;
      this.loginData.password = password;

      const meta = await getClientDeviceMeta();
      const data = {
        username,
        password,
        rememberMe: true,
        deviceName: meta.deviceName || undefined,
        platform: meta.platform || undefined,
        appVersion: meta.appVersion || undefined,
        appBuild: meta.appBuild || undefined,
      };

      try {
        const response = await CapacitorHttp.post({
          url: `${process.env.VUE_APP_API_URL}api/authenticate`,
          data,
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.status !== 200 && response.status !== 1001) {
          this.showAlert('Ошибка входа', 'Проверьте логин и пароль.');
          return;
        }
        await this.applyJwtFromResponse(response);
      } catch (err: any) {
        if (err?.message === 'Network Error' || err?.status === 408) {
          this.$router.replace('/badconnection');
        } else {
          this.showAlert('Ошибка входа', 'Проверьте логин и пароль.');
        }
      } finally {
        this.inlineLoading = false;
      }
    },

    successLogin() {
      this.inlineLoading = false;
      this.finishSessionRestore();
      void registerAdminPushNotifications().then(() => flushPendingPushOpen());
      void flushPendingDeepLink();

      this.$nextTick(function () {
        if (!this.globalContext.offlineMode) {
          this.accountService
            .hasAnyAuthorityAndCheckAuth('ROLE_MANAGER')
            .then((value) => {
              this.globalContext.isManager = value;
            });
        } else {
          this.globalContext.isManager = false;
        }

        const redirect = this.parseSafeRedirectPath(this.$route.query.redirect);
        const fromShowcase = this.$route.query.fromShowcase === '1';
        if (redirect) {
          if (fromShowcase) {
            void router.replace({ name: 'Showcase' }).then(() => {
              void router.push(redirect);
            });
          } else {
            void router.replace(redirect);
          }
        } else {
          void router.replace({ name: 'Showcase' });
        }
      });
    },

    parseSafeRedirectPath(raw: unknown): string | null {
      if (raw == null || typeof raw !== 'string') return null;
      let p = raw.trim();
      try {
        p = decodeURIComponent(p).trim();
      } catch {
        return null;
      }
      if (!p.startsWith('/') || p.startsWith('//')) return null;
      if (/^\/events\/\d+$/.test(p)) return p;
      if (/^\/subscriptions\/\d+$/.test(p)) return p;
      if (/^\/messenger\/invite\/[A-Za-z0-9_-]+$/.test(p)) return p;
      return null;
    },

    async register() {
      await Browser.open({ url: `${process.env.VUE_APP_REGISTER_URL}` }).catch((err) => {
        console.log(err);
      });
    },

    async showAlert(alertTitle: string, alertMessage: string) {
      const alert = await alertController.create({
        header: alertTitle,
        message: alertMessage,
        buttons: ['OK'],
      });
      await alert.present();
    },
  },
});
</script>

<style scoped>
.login-boot-bridge {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: calc(24px + env(safe-area-inset-top, 0px)) 24px calc(24px + env(safe-area-inset-bottom, 0px));
  background: var(--liga-page-bg, #121212);
  color: var(--liga-chrome-text);
}
.login-boot-logo {
  width: min(220px, 58vw);
  height: auto;
}
.login-boot-spinner {
  width: 36px;
  height: 36px;
  color: var(--ion-color-orange);
}
.login-boot-text {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--liga-chrome-text-muted);
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}

.logo {
  margin: 10vh 0 -6vh 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

.logo img {
  width: 300px;
  max-width: 72vw;
  height: auto;
}

.half-screen-top {
  margin-top: 50%;
}

ion-card-header {
  letter-spacing: 0.08em;
  font-size: 2em;
  color: var(--liga-chrome-text);
}

.login-field {
  --background: var(--liga-float-glass-bg-strong);
  --border-radius: 25px;
  --border-width: 1px;
  --border-color: var(--liga-float-glass-border) !important;
  --border-style: solid;
  --color: var(--liga-chrome-text);
  --placeholder-color: var(--liga-chrome-text-muted);
  --highlight-color: transparent;
  --highlight-color-focused: transparent;
  --highlight-height: 0px;
  --padding-start: 1.25em;
  --padding-end: 1.25em;
  margin-bottom: 1.25em;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  overflow: hidden;
  border-radius: 25px;
}

.login-field.ion-focused,
.login-field.has-focus {
  --border-color: rgba(235, 89, 82, 0.55) !important;
}

.login-field::part(native) {
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent;
}

.otp-hint {
  margin: 0 0 1rem;
  text-align: center;
  color: var(--liga-chrome-text-muted);
  font-size: 0.9rem;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  word-break: break-all;
}

.otp-email-hint {
  margin: 0.35rem 0 0.75rem;
  text-align: center;
  color: var(--liga-chrome-text-muted);
  font-size: 0.78rem;
  line-height: 1.35;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}

.otp-boxes {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 1rem;
}

.otp-box {
  width: 44px;
  height: 52px;
  border-radius: 12px;
  border: 1px solid var(--liga-float-glass-border);
  background: var(--liga-float-glass-bg-strong);
  color: var(--liga-chrome-text);
  font-size: 1.35rem;
  font-weight: 700;
  text-align: center;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  outline: none;
  box-sizing: border-box;
}

.otp-box:focus {
  border-color: var(--ion-color-orange);
  box-shadow: 0 0 0 2px rgba(232, 93, 4, 0.25);
}

.otp-error {
  margin: 0 0 0.75rem;
  text-align: center;
  color: #eb445a;
  font-size: 0.85rem;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}

.otp-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.auth-link-btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--ion-color-orange);
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
}

.auth-link-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.auth-switch {
  margin-top: 1.25rem;
  text-align: center;
  color: var(--liga-chrome-text);
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

ion-button {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --color: #ffffff;
  width: 280px;
  height: 50px;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-weight: bold;
  --border-radius: 14px;
}

.main-button-container {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}

.signup-container {
  margin-top: 2vh;
  display: flex;
  flex: row nowrap;
  justify-content: space-between;
  color: var(--liga-chrome-text);
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  cursor: pointer;
}

.not-authorized {
  color: var(--liga-chrome-text-muted);
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  cursor: pointer;
}

.login-card-wrapper {
  z-index: 10;
}

.login-legal {
  padding: 2vh 16px 4vh;
  color: var(--liga-chrome-text-muted);
  font-size: 0.9rem;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}
.login-legal-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5em 1em;
  margin-bottom: 1.5em;
}
.login-legal-link {
  color: var(--ion-color-orange);
  cursor: pointer;
  text-decoration: none;
}
.login-legal-link:hover {
  text-decoration: underline;
}
.login-legal-payee {
  text-align: center;
  font-size: 0.85em;
  line-height: 1.5;
  opacity: 0.95;
}
.login-legal-payee-title {
  font-weight: 600;
  margin-bottom: 0.25em;
}
.login-legal-payee-name {
  font-weight: 600;
  margin-bottom: 0.35em;
}
.login-legal-payee p {
  margin: 0.2em 0;
}

@media (min-width: 768px) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}
</style>
